import React from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { XCircle, Loader2 } from 'lucide-react';
import { StatusBadge, SourceBadge, CANCELLABLE_STATUSES } from './_status.jsx';

function OrderDetailRow({ line, onCancelLine, cancellingLineUid, canCancelLines }) {
  const isCancelled = line.status === 'cancelled';
  const lineCancellable = canCancelLines
    && !isCancelled
    && line.status !== 'invoiced'
    && !line.linked_invoice_id
    && !line.linked_delivery_challan_id;
  const isRepeat = !isCancelled && !!line.repeat_pending;
  const prevOrders = line.repeat_prev_orders || [];
  return (
    <tr className={`border-t ${isCancelled ? 'bg-red-50 text-slate-400 line-through' : ''} ${isRepeat ? 'bg-red-100/70 border-l-4 border-l-red-600' : ''}`}
        data-testid={`order-line-row-${line._uid}`}>
      <td className="px-2 py-2">
        <p className="font-medium">{line.product_name}</p>
        <p className="text-xs text-slate-400">{line.product_sku}</p>
        {isRepeat && (
          <div className="mt-1 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-1 max-w-[240px]"
            data-testid={`line-repeat-note-${line._uid}`}>
            <span className="font-semibold uppercase">Repeated within 15d</span>
            {prevOrders.slice(0, 2).map((p) => (
              <p key={p.order_id} className="mt-0.5">
                Prev: <span className="font-mono">{p.order_number}</span>
                {' '}({String(p.created_at || '').slice(0, 10)}) · {p.pending_qty} pending
              </p>
            ))}
          </div>
        )}
        {isCancelled && (
          <p className="text-[10px] text-red-700 mt-0.5 no-underline font-semibold">
            CANCELLED{line.cancellation_reason ? ` · ${line.cancellation_reason}` : ''}
          </p>
        )}
      </td>
      <td className="px-2 py-2 text-center">{line.quantity}</td>
      <td className="px-2 py-2 text-center text-emerald-700">{line.allocated_qty}</td>
      <td className="px-2 py-2 text-center text-amber-700">{line.pending_qty}</td>
      <td className="px-2 py-2">
        <StatusBadge status={line.status} />
        {/* QA rejection context — surface the reason (and free-text remarks
            entered by the QA operator) right under the badge so the
            franchise / accounts team understands WHY this line never made
            it to invoice. Mirrors the qa_status / qa_remarks fields the
            backend stamps in routes/orders.py::qa_submit. */}
        {(line.qa_status || line.qa_remarks) && (
          line.status === 'qa_rejected'
          || line.status === 'manually_billed'
          || line.qa_status === 'rejected'
          || line.qa_status === 'physical_not_available'
        ) && (
          <div
            className="mt-1 text-[11px] text-red-700 leading-tight max-w-[220px]"
            data-testid={`line-qa-remarks-${line._uid}`}
          >
            {line.qa_status && (
              <span className="font-semibold uppercase">
                {String(line.qa_status).replace(/_/g, ' ')}
              </span>
            )}
            {line.qa_remarks && (
              <p className="text-slate-600 italic mt-0.5 break-words">
                “{line.qa_remarks}”
              </p>
            )}
          </div>
        )}
        {/* Blocker reason for pending_procurement lines (Feb 2026): explains
            WHY the allocation engine can't upgrade this line. Stamped by
            GET /api/orders/{id} at read time (see routes/orders.py). */}
        {line.status === 'pending_procurement' && line._blocker_reason && (
          <div
            className="mt-1 text-[11px] text-amber-800 leading-tight max-w-[240px] bg-amber-50 border border-amber-200 rounded px-1.5 py-1"
            data-testid={`line-blocker-${line._uid}`}
            title={line._blocker_reason}
          >
            <span className="font-semibold">Why not allocated:</span>{' '}
            {line._blocker_reason}
          </div>
        )}
      </td>
      <td className="px-2 py-2 font-mono text-xs">{line.linked_po_number || '—'}</td>
      <td className="px-2 py-2 font-mono text-xs">{line.linked_invoice_number || '—'}</td>
      <td className="px-2 py-2 text-center">
        {lineCancellable && (
          <Button
            size="sm"
            variant="ghost"
            className="text-red-600 hover:bg-red-50 h-7 px-2"
            onClick={() => onCancelLine(line)}
            disabled={cancellingLineUid === line._uid}
            data-testid={`cancel-line-${line._uid}`}
            title="Cancel this line only"
          >
            {cancellingLineUid === line._uid
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <XCircle className="w-3.5 h-3.5" />}
          </Button>
        )}
      </td>
    </tr>
  );
}

export default function OrderDetailDialog({
  order, open, onClose, onCancel, onCancelLine, cancellingId, cancellingLineUid,
}) {
  if (!order) return null;
  const rawLines = order.items || [];
  // Show cancelled lines at the bottom so the live work stays at the top.
  const lines = [...rawLines].sort((a, b) => {
    const aC = a.status === 'cancelled' ? 1 : 0;
    const bC = b.status === 'cancelled' ? 1 : 0;
    return aC - bC;
  });
  const cancelledCount = rawLines.filter(l => l.status === 'cancelled').length;
  const liveCount = rawLines.length - cancelledCount;
  const canCancel = CANCELLABLE_STATUSES.has(order.status);

  // Order Value summary (Feb 2026): compute totals locally so the dialog is
  // self-contained regardless of whether the parent passed the list-view
  // shape (has `order_value`) or the raw shape. Also computes per-line
  // fallback price for the new "Sell / Line Value" columns.
  const resolveLinePrice = (ln) =>
    Number(
      ln._display_selling_price
      ?? ln.selling_price
      ?? ln.outward_selling_price
      ?? ln.mrp
      ?? 0
    );
  const computedOrderValue = rawLines.reduce((acc, ln) => {
    if (ln.status === 'cancelled') return acc; // cancelled lines drop out
    return acc + Number(ln.quantity || 0) * resolveLinePrice(ln);
  }, 0);
  const orderValue = Number(
    order.order_value != null ? order.order_value : computedOrderValue
  );
  const totalOrderedQty = rawLines.reduce(
    (acc, ln) => acc + (ln.status === 'cancelled' ? 0 : Number(ln.quantity || 0)),
    0,
  );
  const inr = (n) => Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-auto" data-testid="order-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <span>{order.order_number}</span>
            <StatusBadge status={order.status} />
            <SourceBadge source={order.source} />
            <div className="flex-1" />
            {canCancel && (
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => onCancel(order)}
                disabled={cancellingId === order.id}
                data-testid={`cancel-order-dialog-${order.order_number}`}
              >
                {cancellingId === order.id
                  ? <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  : <XCircle className="w-4 h-4 mr-1" />}
                Cancel Order
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 bg-slate-50 rounded-lg">
            <div>
              <p className="text-xs text-slate-500">Franchise</p>
              <p className="font-medium truncate" title={order.franchise_name}>{order.franchise_name}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Franchise ID</p>
              <p className="font-mono">{order.franchise_business_id}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Line Items · Ordered Qty</p>
              <p className="font-medium">
                {rawLines.length} <span className="text-slate-400">·</span>{' '}
                <span className="tabular-nums">{totalOrderedQty}</span>
              </p>
            </div>
            <div className="text-right md:text-left">
              <p className="text-xs text-slate-500">Order Value</p>
              <p className="text-lg font-bold text-slate-800 tabular-nums"
                 data-testid="order-detail-value-top">₹{inr(orderValue)}</p>
            </div>
          </div>
          {cancelledCount > 0 && order.status !== 'cancelled' && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm"
                 data-testid="order-cancelled-lines-banner">
              <p className="font-semibold text-amber-800">
                {cancelledCount} line{cancelledCount === 1 ? '' : 's'} cancelled · {liveCount} active
              </p>
              <p className="text-xs text-amber-700 mt-1">
                Cancelled lines are crossed out and shown at the bottom. The order remains
                {' '}<b>{order.status?.replace(/_/g, ' ')}</b> because the other line(s) are still open.
              </p>
            </div>
          )}
          {order.status === 'cancelled' && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm">
              <p className="font-semibold text-red-800">Order cancelled</p>
              <p className="text-xs text-red-700 mt-1">
                {order.cancelled_by_name && <>by <b>{order.cancelled_by_name}</b> </>}
                {order.cancelled_at && <>on {order.cancelled_at.split('T')[0]}</>}
              </p>
              {order.cancellation_reason && (
                <p className="text-xs text-red-700 mt-1">Reason: <i>{order.cancellation_reason}</i></p>
              )}
            </div>
          )}
          <table className="w-full text-sm border rounded-lg overflow-hidden">
            <thead className="bg-slate-100">
              <tr>
                <th className="px-2 py-2 text-left">Product</th>
                <th className="px-2 py-2 text-center">Req</th>
                <th className="px-2 py-2 text-center">Alloc</th>
                <th className="px-2 py-2 text-center">Pending</th>
                <th className="px-2 py-2 text-left">Status</th>
                <th className="px-2 py-2 text-left">Linked PO</th>
                <th className="px-2 py-2 text-left">Invoice</th>
                <th className="px-2 py-2 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {lines.map(line => (
                <OrderDetailRow key={line._uid} line={line}
                                onCancelLine={onCancelLine}
                                cancellingLineUid={cancellingLineUid}
                                canCancelLines={order.status !== 'cancelled'} />
              ))}
            </tbody>
          </table>
          {/* Footer total per spec (Feb 2026): mirror the top-of-dialog value
              so users can see the Order Value at the bottom of the line list
              without scrolling back up. Cancelled lines are excluded to
              match the calculation used by the Franchise Orders card. */}
          <div className="flex items-center justify-end gap-3 pt-2 border-t"
               data-testid="order-detail-value-footer">
            <span className="text-sm text-slate-500">Total Order Value :</span>
            <span className="text-xl font-bold text-slate-800 tabular-nums">₹{inr(orderValue)}</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
